"use client";

import { createContext, ReactNode, useContext } from "react";
import { useFetchProjectEditAccess } from "@/app/hooks/fetchs/project/useFetchProjectEditAccess";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { PublicAffiliation } from "@/app/types/common/PublicAffiliation";
import { Tenant } from "@/app/types/company/Tenant";

type BrandContextValue = {
  tenant: Tenant | null;
  canManageCompany: boolean;
  isCompanyOwner: boolean;
  canSpendCredits: boolean;
  canEditContent: boolean;
  isProjectGuest: boolean;
  companyAffiliation: PublicAffiliation | null;
};

const BrandContext = createContext<BrandContextValue>({
  tenant: null,
  canManageCompany: false,
  isCompanyOwner: false,
  canSpendCredits: true,
  canEditContent: true,
  isProjectGuest: false,
  companyAffiliation: null,
});

// The current subdomain's branding (name / color / logo), or null on the main domain.
export function useBrand(): Tenant | null {
  return useContext(BrandContext).tenant;
}

// Whether the logged-in user manages (OWNER/ADMIN) the current subdomain's company.
export function useCanManageCompany(): boolean {
  return useContext(BrandContext).canManageCompany;
}

// Whether the logged-in user is the OWNER of the current subdomain's company.
export function useIsCompanyOwner(): boolean {
  return useContext(BrandContext).isCompanyOwner;
}

export function useCompanyAffiliation(): PublicAffiliation | null {
  return useContext(BrandContext).companyAffiliation;
}

// Whether the logged-in user may launch credit-consuming actions on this subdomain
// (owner, or a company member the owner shared credits with). External members cannot.
export function useCanSpendCredits(): boolean {
  return useContext(BrandContext).canSpendCredits;
}

// Single source of truth for gating any credit-consuming tool action (run analysis,
// extract avatar, find prospects, and future tools). On the main domain everyone can;
// on a subdomain only the owner and admins with shared credits can. Viewers (plain
// members, unshared admins, external members) get `false` so the launch button is hidden.
export function useCanRunPaidActions(): boolean {
  const { tenant, isCompanyOwner, canSpendCredits } = useContext(BrandContext);
  return tenant === null || isCompanyOwner || canSpendCredits;
}

// A company member's access is company-wide and the layout resolved it; an external's lives on
// their ProjectMember, so it takes the current project to answer, and only they pay that read.
export function useCanEditContent(): boolean {
  const { tenant, canEditContent, isProjectGuest } = useContext(BrandContext);
  const projectId = ProjectStore((state) => state.currentProject?.id);

  const { data: canEditProject } = useFetchProjectEditAccess({
    projectId,
    enabled: isProjectGuest,
  });

  return tenant === null || canEditContent || canEditProject === true;
}

export function useCanManageWorkspace(): boolean {
  const { tenant, canManageCompany } = useContext(BrandContext);
  return tenant === null || canManageCompany;
}

type Props = {
  tenant: Tenant | null;
  canManageCompany: boolean;
  isCompanyOwner: boolean;
  canSpendCredits: boolean;
  canEditContent: boolean;
  isProjectGuest: boolean;
  companyAffiliation: PublicAffiliation | null;
  children: ReactNode;
};

export default function BrandProvider(props: Props) {
  const {
    tenant,
    canManageCompany,
    isCompanyOwner,
    canSpendCredits,
    canEditContent,
    isProjectGuest,
    companyAffiliation,
    children,
  } = props;
  return (
    <BrandContext.Provider
      value={{
        tenant,
        canManageCompany,
        isCompanyOwner,
        canSpendCredits,
        canEditContent,
        isProjectGuest,
        companyAffiliation,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}
